/**
 * HOOK: useEmailTemplates
 * CRUD léger sur la table email_templates (item #7 retours client).
 * Fournit list/create/update/remove + refresh, sans pagination — la table
 * est attendue de petite taille (quelques dizaines de templates max).
 */

import { useCallback, useEffect, useState } from 'react';
import { useSupabase } from './useSupabase';
import type {
  CreateEmailTemplateDto,
  EmailTemplate,
  UpdateEmailTemplateDto,
} from '@/types/emailTemplate';

interface UseEmailTemplatesReturn {
  templates: EmailTemplate[];
  loading: boolean;
  error: Error | null;
  refresh: () => Promise<void>;
  create: (dto: CreateEmailTemplateDto) => Promise<EmailTemplate | null>;
  update: (id: string, dto: UpdateEmailTemplateDto) => Promise<EmailTemplate | null>;
  remove: (id: string) => Promise<boolean>;
}

export function useEmailTemplates(): UseEmailTemplatesReturn {
  const supabase = useSupabase();
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    setError(null);
    const { data, error: fetchError } = await supabase
      .from('email_templates')
      .select('*')
      .order('name', { ascending: true });

    if (fetchError) {
      setError(new Error(fetchError.message));
      setTemplates([]);
    } else {
      setTemplates((data as EmailTemplate[]) || []);
    }
    setLoading(false);
  }, [supabase]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const create = useCallback(
    async (dto: CreateEmailTemplateDto): Promise<EmailTemplate | null> => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      const { data, error: insertError } = await supabase
        .from('email_templates')
        .insert({ ...dto, created_by: user?.id || null })
        .select()
        .single();

      if (insertError) {
        setError(new Error(insertError.message));
        return null;
      }
      await fetchTemplates();
      return data as EmailTemplate;
    },
    [supabase, fetchTemplates]
  );

  const update = useCallback(
    async (id: string, dto: UpdateEmailTemplateDto): Promise<EmailTemplate | null> => {
      const { data, error: updateError } = await supabase
        .from('email_templates')
        .update(dto)
        .eq('id', id)
        .select()
        .single();

      if (updateError) {
        setError(new Error(updateError.message));
        return null;
      }
      await fetchTemplates();
      return data as EmailTemplate;
    },
    [supabase, fetchTemplates]
  );

  const remove = useCallback(
    async (id: string): Promise<boolean> => {
      const { error: deleteError } = await supabase.from('email_templates').delete().eq('id', id);

      if (deleteError) {
        setError(new Error(deleteError.message));
        return false;
      }
      await fetchTemplates();
      return true;
    },
    [supabase, fetchTemplates]
  );

  return {
    templates,
    loading,
    error,
    refresh: fetchTemplates,
    create,
    update,
    remove,
  };
}
